import React, { useEffect, useState } from "react";
import api from "../api";

/* ================== UI PARTS ================== */

function Donut({ value = 0, color = "#3b82f6" }) {
  const size = 86;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const dash = (pct / 100) * c;

  return (
    <div className="relative grid place-items-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`}
        />
      </svg>

      <div className="absolute text-center">
        <div className="text-[18px] font-semibold text-gray-800">{pct}%</div>
      </div>
    </div>
  );
}

function MiniBars({ planned = 0, actual = 0, color = "#3b82f6" }) {
  const p = Number(planned) || 0;
  const a = Number(actual) || 0;
  const max = Math.max(p, a, 1);

  const pH = Math.round((p / max) * 50);
  const aH = Math.round((a / max) * 50);

  return (
    <div className="flex items-end justify-center gap-7">
      {/* Planned */}
      <div className="w-[68px] text-center">
        <div className="text-[11px] font-medium text-gray-700 mb-1">{p}</div>
        <div className="h-[56px] rounded-sm bg-gray-100 flex items-end overflow-hidden">
          <div style={{ height: `${pH}px`, background: `${color}55` }} className="w-full" />
        </div>
        <div className="mt-1 text-[11px] text-gray-500">Planned</div>
      </div>

      {/* Actual */}
      <div className="w-[68px] text-center">
        <div className="text-[11px] font-medium text-gray-700 mb-1">{a}</div>
        <div className="h-[56px] rounded-sm bg-gray-100 flex items-end overflow-hidden">
          <div style={{ height: `${aH}px`, background: color }} className="w-full" />
        </div>
        <div className="mt-1 text-[11px] text-gray-500">Actual</div>
      </div>
    </div>
  );
}

function RiskList({ risks, color = "#3b82f6" }) {
  const rows = [
    { key: "extreme", label: "Extreem" }, // screenshot spelling
    { key: "high", label: "High" },
    { key: "medium", label: "Medium" },
    { key: "low", label: "Low" },
  ];

  const max = Math.max(...rows.map((r) => Number(risks?.[r.key] ?? 0)), 1);

  return (
    <div className="space-y-2">
      {rows.map((r) => {
        const v = Number(risks?.[r.key] ?? 0);
        const w = Math.round((v / max) * 100);

        return (
          <div key={r.key} className="grid grid-cols-[60px_1fr_18px] items-center gap-2">
            <div className="text-[11px] text-gray-600">{r.label}</div>
            <div className="h-[6px] bg-gray-100 overflow-hidden rounded-sm">
              <div
                className="h-full"
                style={{
                  width: `${w}%`,
                  background: color,
                  opacity: r.key === "low" ? 0.35 : r.key === "medium" ? 0.55 : 0.9,
                }}
              />
            </div>
            <div className="text-[11px] text-gray-600 text-right">{v || ""}</div>
          </div>
        );
      })}
    </div>
  );
}

function Section({ children }) {
  return <div className="px-4 py-4">{children}</div>;
}

function Divider() {
  return <div className="h-px bg-gray-200" />;
}

function ProjectColumn({ project }) {
  const {
    name,
    headerColor,
    accent,
    start,
    end,
    progressPct,
    inProgressText,
    plannedHours,
    actualHours,
    risks,
  } = project || {};

  return (
    <div className="w-[255px] bg-white">
      {/* Colored header bar */}
      <div
        className="h-10 flex items-center justify-center text-sm font-semibold text-white"
        style={{ background: headerColor || "#334155" }}
      >
        {name || "—"}
      </div>

      {/* Start / End */}
      <Section>
        <div className="grid grid-cols-2 gap-4 text-[11px]">
          <div>
            <div className="text-gray-400">Start</div>
            <div className="font-semibold text-gray-700">{start || "—"}</div>
          </div>
          <div>
            <div className="text-gray-400">End</div>
            <div className="font-semibold text-gray-700">{end || "—"}</div>
          </div>
        </div>
      </Section>

      <Divider />

      {/* Progress */}
      <Section>
        <div className="flex flex-col items-center">
          <Donut value={progressPct || 0} color={accent || "#334155"} />
          <div className="mt-2 text-[11px] text-gray-500">{inProgressText || "—"}</div>
        </div>
      </Section>

      <Divider />

      {/* Planned vs Actual */}
      <Section>
        <MiniBars
          planned={plannedHours || 0}
          actual={actualHours || 0}
          color={accent || "#334155"}
        />
      </Section>

      <Divider />

      {/* Risks & Issues */}
      <Section>
        <RiskList risks={risks || {}} color={accent || "#334155"} />
      </Section>
    </div>
  );
}

/* ================== MAIN COMPONENT ================== */

export default function ProjectKPIDashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const palette = [
    { headerColor: "#2b6cb0", accent: "#2b6cb0" },
    { headerColor: "#2f855a", accent: "#2f855a" },
    { headerColor: "#7c9a2d", accent: "#7c9a2d" },
    { headerColor: "#f59e0b", accent: "#f59e0b" },
    { headerColor: "#b91c1c", accent: "#b91c1c" },
    { headerColor: "#334155", accent: "#334155" },
  ];

  const fetchKPI = async () => {
    try {
      setErr("");
      setLoading(true);

      const res = await api.get("/project/kpi-dashboard");
      const list = res.data?.projects || [];

      const mapped = list.map((p, idx) => ({
        ...p,
        headerColor: palette[idx % palette.length].headerColor,
        accent: palette[idx % palette.length].accent,
        start: p.start
          ? new Date(p.start).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "2-digit",
            })
          : "—",
        end: p.end
          ? new Date(p.end).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "2-digit",
            })
          : "—",
      }));

      setProjects(mapped);
    } catch (e) {
      const msg =
        e?.response?.data?.message || e?.message || "Failed to load KPI dashboard";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-white">
      {/* Container like screenshot */}
      <div className="border border-gray-200 rounded-sm overflow-hidden">
        <div className="grid grid-cols-[160px_1fr]">
          {/* LEFT SIDEBAR */}
          <div className="bg-gray-100 border-r border-gray-200">
            <div className="h-10 flex items-center px-4 text-xs font-semibold text-gray-600">
              PROJECTS <span className="ml-1 text-gray-400">›</span>
            </div>

            {/* Sidebar rows to match height with sections */}
            <div className="px-4">
              <div className="py-4 text-xs text-gray-500">KPI <span className="ml-1 text-gray-400">›</span></div>
              <div className="h-px bg-gray-200" />

              <div className="py-8 text-sm font-semibold text-gray-700">Progress</div>
              <div className="h-px bg-gray-200" />

              <div className="py-8 text-xs text-gray-600">
                Planned <span className="text-gray-400">vs</span> Actual <br />
                Hours
              </div>
              <div className="h-px bg-gray-200" />

              <div className="py-8 text-sm font-semibold text-gray-700">Risks & Issues</div>
            </div>
          </div>

          {/* RIGHT AREA */}
          <div className="overflow-x-auto">
            <div className="flex">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="border-r border-gray-200 last:border-r-0">
                    <div className="w-[255px] bg-white">
                      <div className="h-10 bg-gray-200 animate-pulse" />
                      <div className="px-4 py-4 space-y-4">
                        <div className="h-10 bg-gray-100 animate-pulse rounded-sm" />
                        <div className="h-24 bg-gray-100 animate-pulse rounded-sm" />
                        <div className="h-20 bg-gray-100 animate-pulse rounded-sm" />
                        <div className="h-24 bg-gray-100 animate-pulse rounded-sm" />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <>
                  {projects.map((p) => (
                    <div
                      key={p.id}
                      className="border-r border-gray-200 last:border-r-0"
                    >
                      <ProjectColumn project={p} />
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Top actions (optional) */}
      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={fetchKPI}
          disabled={loading}
          className="rounded-sm border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          {loading ? "Loading..." : "Refresh"}
        </button>

        {err && <div className="text-sm text-red-600">{err}</div>}
      </div>
    </div>
  );
}
